import { createContext, useContext, useState, ReactNode } from "react";
import type { ShippingAddress } from '@stripe/stripe-js';

interface CartItem {
  productId: string;
  quantity: number;
}

interface CartContextType {
  cart: {
    products: CartItem[];
  };
  shippingAddress: ShippingAddress | null;
  customerEmail: string;
  addToCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  removeFromCart: (productId: string) => void;
  clearCart: () => void;
  setShippingAddress: (address: ShippingAddress | null) => void;
  setCustomerEmail: (email: string) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<{ products: CartItem[] }>({
    products: [],
  });
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress | null>(null);
  const [customerEmail, setCustomerEmail] = useState<string>("");

  const addToCart = (productId: string) => {
    setCart((prev) => {
      const existingItem = prev.products.find((item) => item.productId === productId);
      if (existingItem) {
        return {
          products: prev.products.map((item) =>
            item.productId === productId
              ? { ...item, quantity: item.quantity + 1 }
              : item
          ),
        };
      }
      return {
        products: [...prev.products, { productId, quantity: 1 }],
      };
    });
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart((prev) => ({
      products: prev.products.map((item) =>
        item.productId === productId ? { ...item, quantity } : item
      ),
    }));
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => ({
      products: prev.products.filter((item) => item.productId !== productId),
    }));
  };

  const clearCart = () => {
    setCart({ products: [] });
    setShippingAddress(null);
    setCustomerEmail("");
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        shippingAddress,
        customerEmail,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        setShippingAddress,
        setCustomerEmail,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
